/**
 * DYD Assistant components.
 *  - DydWhiteRemovalFilter: SVG filter that turns white pixels transparent so
 *    the JPG avatars (mascot ball + humanoid) blend onto the dark UI.
 *  - DydChatPanel: shared chat surface used by both the floating mascot
 *    bubble and the full-page coach. Streams to /api/dyd/chat.
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PaperPlaneTilt, Trash, Sparkle, Lightning } from "@phosphor-icons/react";

export const MASCOT_IMG = "https://customer-assets.emergentagent.com/job_padel-hub-12/artifacts/wcuzlore_0fb0c28d-58eb-4d20-b3f2-9b94c82cbe7a.jpeg";
export const COACH_IMG = "https://customer-assets.emergentagent.com/job_padel-hub-12/artifacts/mbvfmc3x_b5688c69-0451-4180-95a2-3bfbdcce5c5f.jpeg";

/** Renders a global SVG filter once that "keys out" near-white pixels. */
export function DydWhiteRemovalFilter() {
  return (
    <svg width="0" height="0" style={{ position: "absolute", pointerEvents: "none" }} aria-hidden>
      <defs>
        <filter id="dyd-remove-white">
          {/* alpha = 1.05 - R - G - B, clamped → near-white pixels become transparent. */}
          <feColorMatrix
            type="matrix"
            values="
              1 0 0 0 0
              0 1 0 0 0
              0 0 1 0 0
              -1 -1 -1 0 1.05
            "
          />
        </filter>
      </defs>
    </svg>
  );
}

/** Avatar image that uses the white-removal filter for dark backgrounds. */
export function DydAvatar({ src, alt = "DYD", size = 48, className = "", testId }) {
  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      style={{ width: size, height: size, maxWidth: "100%", maxHeight: "100%", filter: "url(#dyd-remove-white)" }}
      className={`object-contain ${className}`}
      data-testid={testId}
    />
  );
}

/** Reusable chat panel for both mascot and coach roles. */
export function DydChatPanel({ role = "mascot", onSendStart, compact = false }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(null);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/dyd/messages?role=${role}`)
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [role]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Extract structured ```ACTION ... ``` block from AI response
  const extractAction = (text) => {
    if (!text) return { clean: text, action: null };
    const re = /```ACTION\s*([\s\S]*?)```/i;
    const m = text.match(re);
    if (!m) return { clean: text, action: null };
    try {
      const action = JSON.parse(m[1].trim());
      return { clean: text.replace(re, "").trim(), action };
    } catch { return { clean: text, action: null }; }
  };

  const runAction = async (action) => {
    if (!action || !action.tool) return;
    setExecuting(action.tool);
    try {
      const { data } = await api.post("/dyd/execute-action", { tool: action.tool, args: action.args || {} });
      if (data.ok) {
        toast.success(data.message || "Listo");
        if (data.redirect) {
          setTimeout(() => navigate(data.redirect), 600);
        }
      } else {
        toast.error(data.message || "No se pudo ejecutar");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al ejecutar");
    } finally {
      setExecuting(null);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput("");
    onSendStart?.();
    const userMsg = { id: `u-${Date.now()}`, from_user: true, text, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    try {
      const { data } = await api.post("/dyd/chat", { message: text, role });
      const aiMsg = { id: `a-${Date.now()}`, from_user: false, text: data.response, created_at: new Date().toISOString() };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "DYD no respondió");
      setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
      setInput(text); // restore so user can retry
    } finally {
      setSending(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("¿Borrar toda la conversación con DYD?")) return;
    try {
      await api.delete(`/dyd/messages?role=${role}`);
      setMessages([]);
      toast.success("Conversación borrada");
    } catch { toast.error("Error"); }
  };

  const greeting = role === "mascot"
    ? "¡Hey! 🎾⚡ Soy DYD Bot. ¿Buscas reta, torneo o saber cómo va tu ranking? Solo dime."
    : "¡Hola! 👋 Soy DYD, tu gestor personal de pádel. ¿Te ayudo con inscripciones, pagos, o tu perfil?";

  return (
    <div className={`flex flex-col ${compact ? "h-[380px]" : "h-[520px]"}`} data-testid={`dyd-chat-${role}`}>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3" data-testid={`dyd-messages-${role}`}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-5 h-5 border-2 border-[#CCFF00] border-t-transparent animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex justify-start">
            <div className="bg-[#CCFF00]/10 border border-[#CCFF00]/30 text-[#CCFF00] text-sm p-3 max-w-[85%] leading-relaxed">
              {greeting}
            </div>
          </div>
        ) : (
          messages.map((m) => {
            const { clean, action } = m.from_user ? { clean: m.text, action: null } : extractAction(m.text);
            return (
              <div key={m.id} className={`flex flex-col ${m.from_user ? "items-end" : "items-start"}`}>
                <div
                  className={`text-sm p-3 max-w-[85%] leading-relaxed whitespace-pre-wrap break-words ${
                    m.from_user
                      ? "bg-[#CCFF00] text-black font-semibold"
                      : "bg-[#1A1A1A] border border-white/10 text-gray-200"
                  }`}
                  data-testid={`dyd-msg-${m.from_user ? "user" : "ai"}`}
                >
                  {clean}
                </div>
                {action && (
                  <Button
                    size="sm"
                    onClick={() => runAction(action)}
                    disabled={executing === action.tool}
                    className="mt-2 bg-[#CCFF00] text-black hover:bg-[#B3E600] uppercase font-bold text-[10px] h-8 disabled:opacity-50"
                    data-testid={`dyd-action-${action.tool}`}
                  >
                    <Lightning size={12} className="mr-1" weight="fill" />
                    {executing === action.tool ? "Ejecutando…" : (action.label || "Ejecutar")}
                  </Button>
                )}
              </div>
            );
          })
        )}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-[#1A1A1A] border border-white/10 text-gray-400 text-sm p-3 inline-flex items-center gap-2">
              <Sparkle size={14} className="text-[#CCFF00] animate-pulse" /> DYD está escribiendo…
            </div>
          </div>
        )}
      </div>
      <div className="border-t border-white/10 p-3 flex gap-2 chat-input-row">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder={role === "mascot" ? "Pregúntame algo..." : "Escribe a tu coach..."}
          disabled={sending}
          autoCorrect="off"
          autoCapitalize="sentences"
          spellCheck={false}
          enterKeyHint="send"
          inputMode="text"
          className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] flex-1 text-base"
          style={{ fontSize: "16px" }}
          data-testid={`dyd-input-${role}`}
        />
        <Button
          onClick={send}
          disabled={sending || !input.trim()}
          className="bg-[#CCFF00] text-black hover:bg-[#B3E600] disabled:bg-gray-700 h-10 w-10 p-0"
          data-testid={`dyd-send-${role}`}
        >
          <PaperPlaneTilt size={16} weight="fill" />
        </Button>
        {messages.length > 0 && (
          <Button
            onClick={clear}
            variant="outline"
            className="border-white/10 text-gray-500 hover:text-red-400 h-10 w-10 p-0"
            data-testid={`dyd-clear-${role}`}
            title="Borrar conversación"
          >
            <Trash size={14} />
          </Button>
        )}
      </div>
    </div>
  );
}
